import { Popover, Toolbar } from '@box/blueprint-web';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
} from '@box/blueprint-web-assets/icons/Fill';
import type { Editor } from '@tiptap/react';
import get from 'lodash/get';
import React from 'react';
import { useIntl } from 'react-intl';
import { ALIGNMENT } from '../../../constants';
import messages from '../../../messages';
import { getActiveAlignment } from '../utils/alignment';
import { resetSelection } from '../utils/selection';

type AlignmentPopoverProps = {
  editor: Editor;
  containerRef: React.RefObject<HTMLElement>;
};

const alignmentIcons = {
  left: AlignLeft,
  center: AlignCenter,
  right: AlignRight,
};

export const AlignmentPopover = ({
  editor,
  containerRef,
}: AlignmentPopoverProps) => {
  const [isAlignmentPopoverOpen, setIsAlignmentPopoverOpen] =
    React.useState(false);

  // Left align button ref for auto focus when the alignment popover is opened.
  const leftAlignButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const intl = useIntl();

  const activeAlignment = getActiveAlignment(editor);
  // When no alignment is selected, default to left; this occurs when the alignment is toggled off.
  const activeAlignmentIcon = get(
    alignmentIcons,
    activeAlignment,
    alignmentIcons.left,
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      editor.commands.focus();
    }
    setIsAlignmentPopoverOpen(open);
  };

  const changeAlignment = (
    alignment: ReturnType<typeof getActiveAlignment>,
  ) => {
    editor.chain().focus().setTextAlign(alignment).run();
    resetSelection(editor);
  };

  const toggleItemProps = {
    [ALIGNMENT.LEFT]: {
      ref: leftAlignButtonRef,
      icon: AlignLeft,
      ariaLabel: intl.formatMessage(
        messages.editorToolbarLeftAlignmentAriaLabel,
      ),
      value: ALIGNMENT.LEFT,
    },
    [ALIGNMENT.CENTER]: {
      icon: AlignCenter,
      ariaLabel: intl.formatMessage(
        messages.editorToolbarCenterAlignmentAriaLabel,
      ),
      value: ALIGNMENT.CENTER,
    },
    [ALIGNMENT.RIGHT]: {
      icon: AlignRight,
      ariaLabel: intl.formatMessage(
        messages.editorToolbarRightAlignmentAriaLabel,
      ),
      value: ALIGNMENT.RIGHT,
    },
  };

  return (
    <Popover.Root
      modal={false}
      open={isAlignmentPopoverOpen}
      onOpenChange={handleOpenChange}
    >
      <Popover.Trigger>
        <Toolbar.DropdownTriggerButton
          aria-label={intl.formatMessage(
            messages.editorToolbarAlignmentAriaLabel,
          )}
          selected={Boolean(activeAlignment)}
        >
          <Toolbar.Icon icon={activeAlignmentIcon} />
          <Toolbar.DropdownIndicator
            direction={isAlignmentPopoverOpen ? 'up' : 'down'}
          />
        </Toolbar.DropdownTriggerButton>
      </Popover.Trigger>

      <Popover.ContentContainer
        align="center"
        container={containerRef.current}
        onOpenAutoFocus={() => {
          leftAlignButtonRef.current?.focus();
        }}
        sideOffset={4}
        data-testid="alignment-popover-container"
      >
        <Toolbar.ToggleGroup type="single" value={activeAlignment}>
          {Object.entries(toggleItemProps).map(([key, itemProps]) => {
            return (
              <Toolbar.ToggleItem
                key={key}
                ref={get(itemProps, 'ref', null)}
                aria-label={itemProps.ariaLabel}
                icon={itemProps.icon}
                value={itemProps.value}
                onClick={() => {
                  changeAlignment(itemProps.value);
                }}
              />
            );
          })}
        </Toolbar.ToggleGroup>
      </Popover.ContentContainer>
    </Popover.Root>
  );
};
